import { signOut, useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { NotifType, notify, notPackageNotify, ResponseType } from "brancy/components/notifications/notificationBox";
import { MethodType } from "brancy/helper/api";
import { clientFetchApi } from "brancy/helper/clientFetchApi";
import { InstagramerAccountInfo, IOrderUserInfo, IRefreshToken, PushNotif } from "brancy/models/interfaces";

type SharedStateContextType = {
  value: PushNotif[];
  setValue: React.Dispatch<React.SetStateAction<PushNotif[]>>;
  userInfo: IOrderUserInfo | null;
  setUserInfo: React.Dispatch<React.SetStateAction<IOrderUserInfo | null>>;
  refreshAccountInfo: () => Promise<boolean>;
};
export const InstaInfoContext = React.createContext<SharedStateContextType | undefined>(undefined);
export const InstaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: session, update } = useSession();
  const router = useRouter();
  const [value, setValue] = React.useState<PushNotif[]>([]);
  const [userInfo, setUserInfo] = React.useState<IOrderUserInfo | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const isUpdatingRef = useRef<boolean>(false);

  const refreshToken = useCallback(
    async (accountInfo?: InstagramerAccountInfo) => {
      if (isUpdatingRef.current) return;
      isUpdatingRef.current = true;

      try {
        const res = await clientFetchApi<boolean, IRefreshToken>("/api/user/RefreshToken", {
          methodType: MethodType.get,
          session: session,
          data: undefined,
          queries: undefined,
          onUploadProgress: undefined,
        });
        if (res.succeeded) {
          const instagramerIds = res.value.role.instagramerIds;
          const instagramerCount = instagramerIds.length;
          const currentIndex = session!.user.currentIndex;
          const newCurrentIndex = instagramerCount === 0 ? -1 : currentIndex >= instagramerCount ? 0 : currentIndex;
          const updatedSession = await update({
            ...session,
            user: {
              //...session?.user,
              expireTime: res.value.expireTime,
              id: res.value.id,
              instagramerIds: res.value.role.instagramerIds,
              accessToken: res.value.token,
              socketAccessToken: res.value.socketAccessToken,
              currentIndex: newCurrentIndex,
              lastUpdate: Date.now(),
              commentPermission: accountInfo?.commentPermission ?? session?.user.commentPermission ?? false,
              insightPermission: accountInfo?.insightPermission ?? session?.user.insightPermission ?? false,
              messagePermission: accountInfo?.messagePermission ?? session?.user.messagePermission ?? false,
              publishPermission: accountInfo?.publishPermission ?? session?.user.publishPermission ?? false,
              website: accountInfo?.website ?? session?.user.website ?? null,
              biography: accountInfo?.biography ?? session?.user.biography ?? null,
            },
          });
          router.replace("/");
        } else notify(res.info.responseType, NotifType.Warning);
      } catch (error) {
        notify(ResponseType.Unexpected, NotifType.Error);
      } finally {
        isUpdatingRef.current = false;
      }
    },
    [session, update, router],
  );

  const fetchTitleInfo = useCallback(async () => {
    if (isUpdatingRef.current) return;
    try {
      const res = await clientFetchApi<boolean, IOrderUserInfo>("/api/account/GetTitleInfo", {
        methodType: MethodType.get,
        session: session,
        data: undefined,
        queries: undefined,
        onUploadProgress: undefined,
      });
      if (res.succeeded) {
        setUserInfo(res.value);
      }
    } catch (_) {}
  }, [session]);

  // Returns false when skipped because another account/token update is in flight.
  const GetAccountInfo = useCallback(async (): Promise<boolean> => {
    if (isUpdatingRef.current) return false;
    isUpdatingRef.current = true;

    try {
      let res = await clientFetchApi<boolean, InstagramerAccountInfo>("Instagramer/Account/GetInfo", {
        methodType: MethodType.get,
        session: session,
        data: undefined,
        queries: undefined,
        onUploadProgress: undefined,
      });
      if (res.info.responseType === ResponseType.Forbidden) {
        await signOut({
          redirect: false, // Don't redirect the user after signing out
        });
      } else if (res.info.responseType === ResponseType.PartnerNotExist) {
        await refreshToken(res.value);
      } else if (!res.succeeded) {
        // Handle HTTP errors
        await update({
          ...session,
          user: {
            ...session?.user,
            error: `Failed to fetch data, status: ${res.statusCode}`,
            loginStatus: res.value.loginStatus,
            lastUpdate: Date.now(),
            profileUrl: res.value.profileUrl,
            packageExpireTime: res.value.packageExpireTime ?? session?.user?.packageExpireTime ?? 0,
            createdTime: res.value.createdTime,
          },
        });
      } else if (res.succeeded) {
        if (res.value.packageExpireTime < Date.now() / 1000 && (res.value.loginByFb || res.value.loginByInsta))
          notPackageNotify();
        await update({
          ...session,
          user: {
            loginStatus: res.value.loginStatus,
            lastUpdate: Date.now(),
            profileUrl: res.value.profileUrl,
            username: res.value.username,
            fullName: res.value.fullName ?? "",
            isShopper: res.value.isShopper,
            hasPackage: res.value.packageExpireTime * 1000 > Date.now(),
            isPrivate: res.value.isPrivate,
            isShopperOrInfluencer: res.value.isShopperOrInfluencer,
            isVerified: res.value.isVerified,
            packageExpireTime: res.value.packageExpireTime, // now available in context
            pk: res.value.pk,
            isInfluencer: res.value.isInfluencer,
            isBusiness: res.value.isBusiness,
            loginByFb: res.value.loginByFb,
            loginByInsta: res.value.loginByInsta,
            roles: res.value.roles,
            isPartner: res.value.isPartner,
            commentPermission: res.value.commentPermission,
            insightPermission: res.value.insightPermission,
            messagePermission: res.value.messagePermission,
            publishPermission: res.value.publishPermission,
            website: res.value.website,
            biography: res.value.biography,
            createdTime: res.value.createdTime,
            businessType: res.value.businessType,
          },
        });
      }
    } catch (error: any) {
    } finally {
      isUpdatingRef.current = false;
    }
    return true;
  }, [session, update, refreshToken]);
  const contextValue = useMemo(
    () => ({ value, setValue, userInfo, setUserInfo, refreshAccountInfo: GetAccountInfo }),
    [value, userInfo, GetAccountInfo],
  );
  useEffect(() => {
    if (!session) return;

    const currentTime = Date.now();
    const lastUpdate = Number(session.user.lastUpdate || 0);
    const expireTime = session.user.expireTime * 1000;

    // Prevent multiple rapid calls
    if (isUpdatingRef.current) return;

    // Check if token needs refresh (24 hours before expiry)
    if (expireTime - 864 * 1e6 < currentTime) {
      refreshToken();
    }
    // Check if account info needs update (20 seconds since last update)
    else if (currentTime - lastUpdate > 20000 && session.user.currentIndex > -1) {
      GetAccountInfo();
    }
    // Fetch user title info when in user panel (currentIndex === -1)
    if (session.user.currentIndex === -1 && !userInfo) {
      fetchTitleInfo();
    }
  }, [session, GetAccountInfo, refreshToken, fetchTitleInfo, userInfo]);

  return <InstaInfoContext value={contextValue}>{children}</InstaInfoContext>;
};
